/**
 * useShoppingMutations — TanStack Query mutation wrappers for shopping
 * CRUD with optimistic updates + 409 conflict reconciliation (Plan 04-03).
 *
 * Wires the 5 endpoints from Plan 04-02:
 *   - `addItem`     POST   /api/shopping/items
 *   - `checkItem`   PATCH  /api/shopping/items/:id        (toggles checked)
 *   - `updateItem`  PATCH  /api/shopping/items/:id        (text/category/sort)
 *   - `removeItem`  DELETE /api/shopping/items/:id?version=N
 *   - `sweep`       POST   /api/shopping/sweep
 *
 * Every mutation:
 *   - sends `credentials: 'include'` so the session cookie travels through
 *     SW Background Sync replay verbatim (D-09 + T-04-06)
 *   - PATCH/DELETE bodies carry the locally-observed `version` (D-10)
 *   - runs onMutate (optimistic), onSuccess (replace cache), onError
 *     (rollback or 409-reconcile)
 *
 * 409 handling:
 *   - server returns `{kind:'stale_version', server_version, client_version, item}`
 *   - mutationFn throws an Error with `.canonical = body.item` and `.kind`
 *   - onError adopts the canonical row silently for benign reconciliations;
 *     for non-trivial text edits (>3-char diff) and delete-vs-edit collisions
 *     it pushes a one-shot `lastConflict` into `useShoppingStore` so the
 *     `<ConflictToast>` can surface it
 *
 * D-09 offline path: when the browser is offline the SW Workbox queue
 * intercepts the request — the mutationFn still resolves successfully
 * because Workbox returns a synthetic 200 from the queue. The optimistic
 * cache write is what the user sees; the actual reconciliation arrives via
 * the WS broadcast or the post-reconnect `?since=` delta query (Plan 04-04).
 *
 * Note: `checkItem.onMutate` flips `checked` 0↔1 in cache but DOES NOT
 * bump the `version` — server is authoritative on version; the still-old
 * version in cache is what the next user action would send if they re-tap
 * before the server response arrives. The 409 path handles that loop.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ShoppingItem, CategoryKey } from '@stell/shared';
import { useShoppingStore, type ShoppingConflict } from './shoppingStore';

/** Local error tag carried by 409 conflict throws. */
type ConflictError = Error & {
  canonical?: ShoppingItem;
  kind?: 'stale_version';
};

/**
 * Optimistic-cache row shape — extends the canonical ShoppingItem with
 * a transient `_pending` flag the OfflinePendingChip reads. Plan 04-04
 * D-09: when the SW Workbox queue catches a network error, the optimistic
 * row stays put with `_pending: true` until the WS broadcast (or the
 * post-reconnect `?since=` delta read) replaces it with the canonical row.
 */
type OptimisticItem = ShoppingItem & { _pending?: boolean };

/** Heuristic: was the failure a fetch network error (vs an HTTP-status throw)? */
function isNetworkError(err: unknown): boolean {
  if (err instanceof TypeError) return true;
  const msg = (err as { message?: string } | null)?.message ?? '';
  return /Failed to fetch|NetworkError|Network request failed|Load failed/i.test(msg);
}

function tagConflict(canonical: ShoppingItem, kind: 'stale_version'): ConflictError {
  const err = new Error('version_conflict') as ConflictError;
  err.canonical = canonical;
  err.kind = kind;
  return err;
}

async function parseConflict(res: Response): Promise<ShoppingItem | null> {
  if (res.status !== 409) return null;
  try {
    const body = (await res.json()) as { item?: ShoppingItem };
    return body.item ?? null;
  } catch {
    return null;
  }
}

interface AddInput {
  /** Client-generated UUID — D-09: lets offline-create round-trip via Workbox replay. */
  id: string;
  text: string;
  category?: CategoryKey | null;
  category_overridden?: boolean;
  sort_order?: number;
}

interface UpdatePatch {
  text?: string;
  category?: CategoryKey | null;
  category_overridden?: boolean;
  sort_order?: number;
}

const QK = ['shopping', 'items'] as const;

export function useShoppingMutations() {
  const qc = useQueryClient();
  const setConflict = useShoppingStore((s) => s.setConflict);

  /** POST /api/shopping/items — D-04. */
  const addItem = useMutation({
    mutationFn: async (input: AddInput): Promise<ShoppingItem> => {
      const res = await fetch('/api/shopping/items', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw new Error(`shopping_add_${res.status}`);
      return (await res.json()) as ShoppingItem;
    },
    onMutate: async (input) => {
      await qc.cancelQueries({ queryKey: QK });
      const prev = qc.getQueryData<ShoppingItem[]>(QK);
      // Optimistic ghost row — minimal valid shape so the route renders it.
      // `_pending: true` is the OfflinePendingChip signal (Plan 04-04 D-09);
      // onSuccess clears it by replacing with the canonical server row,
      // onError(network) leaves it in place so the SW Workbox queue can
      // replay later and the WS broadcast will eventually deliver the
      // canonical row that drops `_pending`.
      const ghost: OptimisticItem = {
        id: input.id,
        text: input.text,
        category: input.category ?? null,
        category_overridden: input.category_overridden ? 1 : 0,
        checked: 0,
        added_by: 'local',
        added_at: new Date().toISOString(),
        checked_by: null,
        checked_at: null,
        removed_at: null,
        removed_by: null,
        sort_order: input.sort_order ?? Number.MAX_SAFE_INTEGER,
        version: 0,
        updated_at: new Date().toISOString(),
        updated_by: 'local',
        _pending: true,
      };
      qc.setQueryData<ShoppingItem[]>(QK, (old = []) => [...old, ghost]);
      return { prev, optimisticId: input.id };
    },
    onError: (err, _input, ctx) => {
      // Plan 04-04 D-09: network error → SW Workbox queue caught the request.
      // KEEP the optimistic row with `_pending: true` (set in onMutate); the
      // WS broadcast or the post-reconnect `?since=` delta read will replace
      // it with the canonical server row. Rolling back here would erase the
      // user's input the moment they tap Submit while offline — exactly the
      // SHOP-04 failure mode this plan exists to prevent.
      if (isNetworkError(err)) return;
      // HTTP error path (4xx/5xx) — no 409 on add (UUID collision-safe);
      // server validation failure → roll back to the previous cache snapshot.
      if (ctx?.prev !== undefined) qc.setQueryData(QK, ctx.prev);
    },
    onSuccess: (serverRow, _input, ctx) => {
      // Replace the optimistic ghost (matched by id) with the canonical row.
      qc.setQueryData<ShoppingItem[]>(QK, (old = []) =>
        old.map((x) => (x.id === ctx?.optimisticId || x.id === serverRow.id ? serverRow : x)),
      );
    },
  });

  /** PATCH /api/shopping/items/:id — toggle checked (D-04 + D-10). */
  const checkItem = useMutation({
    mutationFn: async (item: ShoppingItem): Promise<ShoppingItem> => {
      const res = await fetch(`/api/shopping/items/${item.id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ checked: !(item.checked === 1), version: item.version }),
      });
      const canonical = await parseConflict(res);
      if (canonical) throw tagConflict(canonical, 'stale_version');
      if (!res.ok) throw new Error(`shopping_check_${res.status}`);
      return (await res.json()) as ShoppingItem;
    },
    onMutate: async (item) => {
      await qc.cancelQueries({ queryKey: QK });
      const prev = qc.getQueryData<ShoppingItem[]>(QK);
      qc.setQueryData<ShoppingItem[]>(QK, (old = []) =>
        old.map((x) =>
          x.id === item.id
            ? { ...x, checked: (x.checked === 1 ? 0 : 1) as 0 | 1 }
            : x,
        ),
      );
      return { prev };
    },
    onError: (err: ConflictError, item, ctx) => {
      if (err.canonical) {
        // Benign idempotent reconciliation — adopt server view silently.
        qc.setQueryData<ShoppingItem[]>(QK, (old = []) =>
          old.map((x) => (x.id === item.id ? (err.canonical as ShoppingItem) : x)),
        );
        return;
      }
      if (ctx?.prev !== undefined) qc.setQueryData(QK, ctx.prev);
    },
    onSuccess: (serverRow) => {
      qc.setQueryData<ShoppingItem[]>(QK, (old = []) =>
        old.map((x) => (x.id === serverRow.id ? serverRow : x)),
      );
    },
  });

  /** PATCH /api/shopping/items/:id — text/category/sort_order edits (D-10 + D-11). */
  const updateItem = useMutation({
    mutationFn: async (args: {
      item: ShoppingItem;
      patch: UpdatePatch;
    }): Promise<ShoppingItem> => {
      const res = await fetch(`/api/shopping/items/${args.item.id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ ...args.patch, version: args.item.version }),
      });
      const canonical = await parseConflict(res);
      if (canonical) throw tagConflict(canonical, 'stale_version');
      if (!res.ok) throw new Error(`shopping_update_${res.status}`);
      return (await res.json()) as ShoppingItem;
    },
    onMutate: async (args) => {
      await qc.cancelQueries({ queryKey: QK });
      const prev = qc.getQueryData<ShoppingItem[]>(QK);
      qc.setQueryData<ShoppingItem[]>(QK, (old = []) =>
        old.map((x) => {
          if (x.id !== args.item.id) return x;
          // Coerce boolean → 0|1 for the category_overridden column shape.
          const merged: ShoppingItem = { ...x };
          if (args.patch.text !== undefined) merged.text = args.patch.text;
          if (args.patch.category !== undefined) merged.category = args.patch.category;
          if (args.patch.sort_order !== undefined) merged.sort_order = args.patch.sort_order;
          if (args.patch.category_overridden !== undefined) {
            merged.category_overridden = args.patch.category_overridden ? 1 : 0;
          }
          return merged;
        }),
      );
      return { prev, prevText: args.item.text, nextText: args.patch.text };
    },
    onError: (err: ConflictError, args, ctx) => {
      if (err.canonical) {
        qc.setQueryData<ShoppingItem[]>(QK, (old = []) =>
          old.map((x) => (x.id === args.item.id ? (err.canonical as ShoppingItem) : x)),
        );
        // D-10: surface text-edit conflict only when local diff is non-trivial (>3 chars).
        const prevText = ctx?.prevText ?? '';
        const nextText = ctx?.nextText ?? prevText;
        if (typeof nextText === 'string' && Math.abs(nextText.length - prevText.length) > 3) {
          const conflict: ShoppingConflict = {
            kind: 'edit',
            nick: err.canonical.updated_by,
          };
          setConflict(conflict);
        }
        return;
      }
      if (ctx?.prev !== undefined) qc.setQueryData(QK, ctx.prev);
    },
    onSuccess: (serverRow) => {
      qc.setQueryData<ShoppingItem[]>(QK, (old = []) =>
        old.map((x) => (x.id === serverRow.id ? serverRow : x)),
      );
    },
  });

  /** DELETE /api/shopping/items/:id?version=N — soft-delete (D-04 + D-10). */
  const removeItem = useMutation({
    mutationFn: async (item: ShoppingItem): Promise<ShoppingItem> => {
      const res = await fetch(
        `/api/shopping/items/${item.id}?version=${item.version}`,
        { method: 'DELETE', credentials: 'include' },
      );
      const canonical = await parseConflict(res);
      if (canonical) throw tagConflict(canonical, 'stale_version');
      if (!res.ok) throw new Error(`shopping_remove_${res.status}`);
      return (await res.json()) as ShoppingItem;
    },
    onMutate: async (item) => {
      await qc.cancelQueries({ queryKey: QK });
      const prev = qc.getQueryData<ShoppingItem[]>(QK);
      qc.setQueryData<ShoppingItem[]>(QK, (old = []) =>
        old.filter((x) => x.id !== item.id),
      );
      return { prev };
    },
    onError: (err: ConflictError, _item, ctx) => {
      if (err.canonical) {
        // Delete-vs-edit collision: peer edited while we removed; toast it.
        const conflict: ShoppingConflict = {
          kind: 'removed',
          nick: err.canonical.updated_by,
        };
        setConflict(conflict);
        // Restore the canonical row to the cache.
        qc.setQueryData<ShoppingItem[]>(QK, (old = []) => {
          const exists = old.some((x) => x.id === err.canonical?.id);
          return exists ? old : [...old, err.canonical as ShoppingItem];
        });
        return;
      }
      if (ctx?.prev !== undefined) qc.setQueryData(QK, ctx.prev);
    },
    onSuccess: () => {
      // Tombstone returned; no cache write needed (we already filtered out
      // in onMutate; WS broadcast will echo `removed` to peers).
    },
  });

  /** POST /api/shopping/sweep — D-04 + D-12. */
  const sweep = useMutation({
    mutationFn: async (): Promise<{ removed: number }> => {
      const res = await fetch('/api/shopping/sweep', {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`shopping_sweep_${res.status}`);
      return (await res.json()) as { removed: number };
    },
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: QK });
      const prev = qc.getQueryData<ShoppingItem[]>(QK);
      qc.setQueryData<ShoppingItem[]>(QK, (old = []) =>
        old.filter((x) => x.checked !== 1),
      );
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev !== undefined) qc.setQueryData(QK, ctx.prev);
    },
    // onSuccess: WS will broadcast `removed` per row; cache already filtered.
  });

  return { addItem, checkItem, updateItem, removeItem, sweep };
}
